import {
  Autocomplete,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import React, { useEffect, useRef, useState } from "react";
import {
  createAddress,
  deleteAddress,
  fetchAddresses,
  selectAddressLoading,
  selectAddresses,
  updateAddress,
} from "../../../redux/slices/addressSlice";
import { useDispatch, useSelector } from "react-redux";

import LoaderMini from "../../Loader/LoaderMini";
import { fetchCities } from "../../../redux/slices/citiesSlice";
import saudi_flag from "../../../assets/icons/saudi_flag.webp";
import { toast } from "react-hot-toast";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const libraries = ["places"];

const ConfirmDeleteModal = ({ onConfirm, onCancel }) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center
  justify-center z-50"
    >
      <div className="bg-white p-6 rounded-2xl shadow-xl text-center space-y-3">
        <h2 className="text-lg font-medium ">
          {t("deleteAddressLabel")}
        </h2>
        <div className="flex justify-center gap-4">
          <button
            onClick={onConfirm}
            className="text-red-500 h-[58px] w-[100px] hover:underline"
          >
            {t("delete")}
          </button>
          <button
            onClick={onCancel}
            className="text-purble-2 h-[58px] w-[100px] hover:underline"
          >
            {t("cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

const MapPicker = ({
  marker,
  setMarker,
  locationLabel,
  setLocationLabel,
  addresses,
  setValue,
}) => {
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { t } = useTranslation();

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setMarker({ lat, lng });
    setLocationLabel(place.formatted_address);
    mapRef.current.panTo({ lat, lng });
  };

  const fetchAddressLabel = async ({ lat, lng }) => {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
    );
    const data = await res.json();
    const label = data?.results?.[0]?.formatted_address || "";
    setLocationLabel(label);
    if (setValue) {
      setValue("description", label); // 👈 autofill description field
    }
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarker({ lat, lng });
    fetchAddressLabel({ lat, lng });
  };

  if (loadError) return <div>{t("errorLaodingMaps")}</div>;
  if (!isLoaded)
    return (
      <div className="flex items-center justify-center w-full h-full ">
        <LoaderMini color="bg-blue-500" />
      </div>
    ); // You can customize this
  return (
    <div className="w-full">
      <Autocomplete
        onLoad={(ref) => (autocompleteRef.current = ref)}
        onPlaceChanged={handlePlaceChanged}
      >
        <input
          type="text"
          placeholder={t("searchLocation")}
          className="w-full px-4 py-2 border rounded-full mb-4"
        />
      </Autocomplete>
      <GoogleMap
        onLoad={(map) => (mapRef.current = map)}
        onClick={handleMapClick}
        center={marker}
        zoom={13}
        mapContainerStyle={{ width: "100%", height: "300px" }}
      >
        <Marker position={marker} />
        {addresses.map((addr, i) => {
          if (!addr.location) return null;
          const [lat, lng] = addr.location.split(",").map(Number);
          return <Marker key={i} position={{ lat, lng }} />;
        })}
      </GoogleMap>
      {locationLabel && (
        <p className="text-sm text-gray-700 italic mt-2">{locationLabel}</p>
      )}
    </div>
  );
};

const Addresses = () => {
  const dispatch = useDispatch();
  const addresses = useSelector(selectAddresses);
  const loading = useSelector(selectAddressLoading);
  const formRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [marker, setMarker] = useState({ lat: 24.7136, lng: 46.6753 });
  const [locationLabel, setLocationLabel] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const {
    cities,
    loading: citiesLoading,
    error: citiesError,
  } = useSelector((state) => state.cities);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    dispatch(fetchAddresses());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchCities());
  }, [dispatch]);

  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  
  const onSubmit = async (formData) => {
    const preparedData = {
      latitude: marker.lat,
      longitude: marker.lng,
      maps_name: locationLabel || formData.description,
      city: formData.city_id,
      description: formData.description,
      type: formData.type || "Home",
      phone: formData.phone
        ? formData.phone.startsWith("+966")
          ? formData.phone
          : `+966${formData.phone}`
        : undefined,
      building_number: formData.building_number,
      apartment_number: formData.apartment_number,
      is_default: formData.is_default || false,
    };

    setSubmitting(true);

    try {
      if (editing) {
        await dispatch(
          updateAddress({ id: editing.id, data: preparedData })
        ).unwrap();
        toast.success("Address updated successfully!");
      } else {
        await dispatch(createAddress(preparedData)).unwrap();
        toast.success("Address created successfully!");
      }
      await dispatch(fetchAddresses());
      reset();
      setEditing(null);
      setSubmitting(false);
      window.scrollTo({
        top: 500,
        left: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to save address");
      setSubmitting(false);
    }
  };

  const startEdit = (addr) => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setEditing(addr);
    if (addr.location) {
      const [lat, lng] = addr.location.split(",").map(Number);
      setMarker({ lat, lng });
    }
    setLocationLabel(addr.maps_name || ""); // 🛠️ Set the map location label too

    // setValue("first_name", addr.first_name || "");
    setValue("phone", addr.phone?.replace("+966", "") || "");
    setValue("building_number", addr.building_number || "");
    setValue("apartment_number", addr.apartment_number || "");
    setValue("is_default", addr.is_default || false);
    setValue("description", addr.description || ""); // 🛠️ Set description for editing
    setValue("type", addr.type || "Home"); // 🛠️ Set address type (default Home)
    setValue("city_id", addr.city.id || ""); // 🛠️ Set city_id
  };

  const cancelEdit = () => {
    reset();
    setEditing(null);
    window.scrollTo({
      top: 500,
      left: 0,
      behavior: "smooth",
    });
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteAddress(confirmDelete)).unwrap();
      toast.success("Address deleted successfully!");
      dispatch(fetchAddresses());
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete address");
    }
    setConfirmDelete(null);
  };

  return (
    <div className="w-full flex flex-col items-start justify-start">
      {loading ? (
        <div className="flex items-center justify-center w-full h-full">
          <LoaderMini color="bg-purble-2" />
        </div>
      ) : (
        <ul
          className="flex start justify-start w-full flex-col rounded-2xl 
        border mb-4"
        >
          {addresses.map((addr, index) => (
            <div
              key={addr.id}
              className={` p-4 flex flex-col gap-2 ${
                index === addresses.length - 1 ? "border-0" : "border-b"
              }`}
            >
              <div className="flex flex-col gap-3">
                <div>
                  <strong>#{addr.id}</strong> - {addr.phone}
                </div>
                <div>{addr.maps_name}</div>
              </div>
              <div className="flex gap-5">
                <button
                  onClick={() => startEdit(addr)}
                  className="text-blue-600 hover:underline"
                >
                  {t("edit")}
                </button>
                <button
                  onClick={() => setConfirmDelete(addr.id)}
                  className="text-red-600 hover:underline"
                >
                  {t("delete")}
                </button>
              </div>
            </div>
          ))}
        </ul>
      )}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col w-full gap-6"
        ref={formRef}
      >
        <div>
          <p className="text-2xl font-semibold">
            {editing ? t("editAddress") : t("addNewAddress")}
          </p>
        </div>

        <div
          className="flex flex-col lg:flex-row items-center justify-center
         w-full gap-2 mb-5"
        >
          <div
            className="w-full flex items-center justify-center flex-col 
          "
          >
            <label
              className="block text-xl w-full text-start font-medium 
            text-black mb-2"
            >
              {t("phone")} *
            </label>
            <div className="relative w-full">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm flex items-center gap-1">
                <img src={saudi_flag} alt="" className="w-8 h-5 object-cover" />
                <p>+966</p>
              </div>
              <input
                {...register("phone", {
                  required: t("requiredPhone"),
                  pattern: {
                    value: /^\d{7,10}$/,
                    message: t("phoneValidation"),
                  },
                })}
                placeholder="966xxxxxxxxx"
                className="w-full pl-24 px-5 py-2 border rounded-full mt-1
                focus:outline-none"
              />
            </div>
            {errors.phone && (
              <p className="text-red-500 w-full text-start text-sm mt-1">
                {errors.phone.message}
              </p>
            )}
          </div>
        </div>

        <MapPicker
          marker={marker}
          setMarker={setMarker}
          locationLabel={locationLabel}
          setLocationLabel={setLocationLabel}
          addresses={addresses}
          setValue={setValue}
        />

        <div className="w-full">
          <label
            className="block text-xl w-full text-start font-medium 
            text-black mb-2 "
          >
            {t("addressDesc")} *
          </label>
          <input
            {...register("description", {
              required:t("requiredAddressDesc"),
            })}
            className="w-full mt-1 px-4 py-2 border rounded-full"
            placeholder={t("addressDescPlaceholder")}
          />
          {errors.description && (
            <p className="text-red-500 text-sm">{errors.description.message}</p>
          )}
        </div>

        <div className="w-full">
          <label
            className="block text-xl w-full text-start font-medium 
            text-black mb-2 "
          >
            {t("addressType")}
          </label>
          <select
            {...register("type")}
            className="w-full mt-1 px-4 py-2 border rounded-full"
          >
            <option value="Home">{t("homeAddress")}</option>
            <option value="Office">{t("officeAddress")}</option>
          </select>
        </div>

        <div className="w-full">
          <label
            className="block text-xl w-full text-start font-medium 
            text-black mb-2 "
          >
            {t('city')} *
          </label>
          <select
            {...register("city_id", { required: t("requiredCity") })}
            className="w-full mt-1 px-4 py-2 border rounded-full"
          >
            <option value="">{t("selectCity")}</option>
            {!citiesLoading &&
              !citiesError &&
              cities?.map((city) => (
                <option key={city.id} value={city.id}>
                  {isEng ? city.e_name : city.a_name}
                </option>
              ))}
          </select>
          {errors.city_id && (
            <p className="text-red-500 text-sm">{errors.city_id.message}</p>
          )}
        </div>

        <div className="w-full">
          <label
            className="block text-xl w-full text-start font-medium 
            text-black mb-2 "
          >
            {t("buildingNumber")}
          </label>
          <input
            {...register("building_number")}
            className="w-full mt-1 px-4 py-2 border rounded-full"
          />
        </div>

        <div className="w-full">
          <label
            className="block text-xl w-full text-start font-medium 
            text-black mb-2 "
          >
            {t("aptNumber")}
          </label>
          <input
            {...register("apartment_number")}
            className="w-full mt-1 px-4 py-2 border rounded-full"
          />
        </div>

        <label className="flex items-center gap-2">
          <input type="checkbox" {...register("is_default")} />
          {t("makeDefaultAddress")}
        </label>

        <div
          className="flex w-full items-center justify-center lg:justify-start gap-2 
        flex-col lg:flex-row"
        >
          <button
            type="submit"
            className="w-full lg:w-[158px] h-[58px] px-4 bg-purble-2 text-white 
            rounded-full hover:bg-blue-600 transition"
          >
            {submitting ? (
              <div className="flex items-center justify-center gap-2">
                <LoaderMini color="bg-white" />
              </div>
            ) : editing ? (
              t("updateAdddress")
            ) : (
              t("saveAddress")
            )}
          </button>
          {editing && (
            <button
              type="button"
              onClick={cancelEdit}
              className="w-full lg:w-[158px] h-[58px] px-4 bg-white  
            rounded-full hover:underline transition"
            >
              {t("cancel")}
            </button>
          )}
        </div>
      </form>
      {confirmDelete && (
        <ConfirmDeleteModal
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default Addresses;
